// Supermove
import {gql} from '@supermove/graphql';
import {useForm, useFormMutation} from '@supermove/hooks';

import RefundPaymentForm, {
  RefundPaymentFormType,
} from '@shared/modules/Refund/forms/RefundPaymentForm';

export class RefundValidationError extends Error {
  errors: any;

  constructor(errors: any) {
    super('Refund validation error');
    this.name = 'RefundValidationError';
    this.errors = errors;
  }
}

const useRefundPaymentMutation = ({
  refundPaymentForm,
  onSuccess,
  onError,
}: {
  refundPaymentForm: RefundPaymentFormType;
  onSuccess: () => void;
  onError: (errors: unknown) => void;
}) => {
  const form = useForm({
    initialValues: {
      refundPaymentForm: RefundPaymentForm.toForm(refundPaymentForm),
    },
  });

  const {submitting, handleSubmit} = useFormMutation({
    form,
    mutation: useRefundPaymentMutation.mutation,
    variables: {
      refundPaymentForm: RefundPaymentForm.toMutation(form.values.refundPaymentForm),
    },
    onSuccess,
    onError,
  });

  const handleRefundCreditCard = async (options?: any) => {
    const response = await handleSubmit(options);
    if (response.errors) {
      throw response.errors;
    }
    if (response.data?.response?.errors) {
      throw new RefundValidationError(response.data.response.errors);
    }
    if (!response.data?.response) {
      throw new Error('Received empty GraphQL response in RefundPaymentMutation');
    }
    return response.data.response;
  };

  return {
    form,
    submitting,
    handleSubmit,
    handleRefundCreditCard,
  };
};

// --------------------------------------------------
// Data
// --------------------------------------------------
useRefundPaymentMutation.mutation = gql`
  mutation useRefundPaymentMutation($refundPaymentForm: RefundPaymentForm!) {
    response: refundPayment(refundPaymentForm: $refundPaymentForm) {
      ${gql.errors}
      refund {
        id
      }
    }
  }
`;

export default useRefundPaymentMutation;
